import React from "react";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import { FiEdit2, FiTrash2, FiEye } from "react-icons/fi";
import { assetUrl } from "@/lib/assets";

export type AirlineTableFilters = {
  code?: string;
  name?: string;
};

export type AirlineRow = {
  id: string | number;
  code: string;
  name: string;
  logoPath?: string | null;
};

function resolveLogoSrc(path?: string | null) {
  const p = String(path ?? "").trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const normalized = p.startsWith("assets/") ? p : `assets/${p.replace(/^\/+/, "")}`;
  return assetUrl(normalized);
}

export default function AirlinesTable({
  pageRows,
  serialStart,
  filters,
  setFilters,
  onView,
  onEdit,
  onDelete,
}: {
  pageRows: AirlineRow[];
  serialStart: number;
  filters: AirlineTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<AirlineTableFilters>>;
  onView: (r: AirlineRow) => void;
  onEdit: (r: AirlineRow) => void;
  onDelete: (r: AirlineRow) => void;
}) {
  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      <div className="hidden overflow-x-auto overflow-visible md:block">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Code</span>
                  <ColumnFilter label="Code">
                    <Input
                      placeholder="Contains..."
                      value={filters.code || ""}
                      onChange={(e) => setFilters((f) => ({ ...f, code: e.target.value }))}
                    />
                  </ColumnFilter>
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains..."
                      value={filters.name || ""}
                      onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
                    />
                  </ColumnFilter>
                </div>
              </Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-500">
                  No airlines found.
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50/60">
                  <Td>{serialStart + i + 1}</Td>
                  <Td className="font-medium text-gray-900">{r.code}</Td>
                  <Td className="text-gray-900">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200">
                        {resolveLogoSrc(r.logoPath) ? (
                          <img
                            src={resolveLogoSrc(r.logoPath)!}
                            alt={`${r.name} logo`}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                      <span>{r.name}</span>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onView(r)} aria-label="View" title="View"><FiEye /></button>
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onEdit(r)} aria-label="Edit" title="Edit"><FiEdit2 /></button>
                      <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50" onClick={() => onDelete(r)} aria-label="Delete" title="Delete"><FiTrash2 /></button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="divide-y divide-gray-200 md:hidden">
        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No airlines found.</div>
        ) : (
          pageRows.map((r, i) => (
            <div key={r.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="pr-2">
                  <div className="text-sm font-medium text-gray-900">{serialStart + i + 1}. {r.code}</div>
                  <div className="mt-1">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200">
                        {resolveLogoSrc(r.logoPath) ? (
                          <img
                            src={resolveLogoSrc(r.logoPath)!}
                            alt={`${r.name} logo`}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>
                      <span className="text-xs text-gray-700">{r.name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onView(r)} aria-label="View"><FiEye /></button>
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100" onClick={() => onEdit(r)} aria-label="Edit"><FiEdit2 /></button>
                  <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50" onClick={() => onDelete(r)} aria-label="Delete"><FiTrash2 /></button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th scope="col" className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}>{children}</th>;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
